import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { API_URL } from '../../config';

function Feedback() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all'); // all, new, in_progress, resolved

  useEffect(() => {
    fetchFeedback();
  }, [filter]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      let url = `${API_URL}/feedback?sort=-createdAt`;
      if (filter !== 'all') {
        url += `&status=${filter}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFeedback(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Fetch feedback error:', err);
      setError('Failed to load feedback');
      setLoading(false);
    }
  };

  const updateFeedbackStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.patch(
        `${API_URL}/feedback/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update feedback list
      setFeedback(feedback.map(item => {
        if (item._id === id) {
          return { ...item, status };
        }
        return item;
      }));
      
      // Update modal if open
      if (showModal && selectedFeedback && selectedFeedback._id === id) {
        setSelectedFeedback({ ...selectedFeedback, status });
      }
    } catch (err) {
      console.error('Update feedback status error:', err);
      alert('Failed to update feedback status');
    }
  };

  const viewFeedback = (item) => {
    setSelectedFeedback(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">New</span>;
      case 'in_progress':
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">In Progress</span>;
      case 'resolved':
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Resolved</span>;
      default:
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600">Loading feedback...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Feedback Management</h1>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">Filter:</span>
          <select
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Feedback</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>
      
      {feedback.length === 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
          <p className="text-gray-500">No feedback found</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {feedback.map((item) => (
              <li key={item._id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-blue-600 truncate cursor-pointer hover:underline" onClick={() => viewFeedback(item)}>
                        {item.name} - {item.feedbackType}
                      </p>
                      <p className="mt-2 flex items-center text-sm text-gray-500">
                        <span>{item.email}</span>
                        <span className="mx-1">•</span>
                        <span>{formatDate(item.createdAt)}</span>
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(item.status)}
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 line-clamp-2">{item.message}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Feedback Detail Modal */}
      {showModal && selectedFeedback && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Feedback Details</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <div className="flex justify-between">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{selectedFeedback.name}</h4>
                    <p className="text-sm text-gray-500">{selectedFeedback.email}</p>
                  </div>
                  {getStatusBadge(selectedFeedback.status)}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Type: <span className="font-medium">{selectedFeedback.feedbackType}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Submitted: <span className="font-medium">{formatDate(selectedFeedback.createdAt)}</span>
                </p>
              </div>
              
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Message:</h5>
                <div className="bg-gray-50 p-3 rounded-md text-gray-800 text-sm whitespace-pre-wrap">
                  {selectedFeedback.message}
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Update Status:</h5>
                <div className="flex space-x-2">
                  <button
                    onClick={() => updateFeedbackStatus(selectedFeedback._id, 'new')}
                    className={`px-3 py-1 text-xs font-medium rounded-md ${
                      selectedFeedback.status === 'new' 
                        ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                        : 'bg-gray-100 text-gray-800 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    New
                  </button>
                  <button
                    onClick={() => updateFeedbackStatus(selectedFeedback._id, 'in_progress')}
                    className={`px-3 py-1 text-xs font-medium rounded-md ${
                      selectedFeedback.status === 'in_progress' 
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
                        : 'bg-gray-100 text-gray-800 hover:bg-yellow-50 hover:text-yellow-600'
                    }`}
                  >
                    In Progress
                  </button>
                  <button
                    onClick={() => updateFeedbackStatus(selectedFeedback._id, 'resolved')}
                    className={`px-3 py-1 text-xs font-medium rounded-md ${
                      selectedFeedback.status === 'resolved' 
                        ? 'bg-green-100 text-green-800 border border-green-300' 
                        : 'bg-gray-100 text-gray-800 hover:bg-green-50 hover:text-green-600'
                    }`}
                  >
                    Resolved
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default Feedback; 